/**
 * Lot-based sellable availability for Checkout / POS (and any sell path).
 * On-hand sellable = lots filtered by isLotIncludedInStockMetrics (+ store).
 * Inventaire KPI may prefer stockSnapshots (same sellable filter for value/current).
 * Not a second stockWrite truth — reads only.
 *
 * Batch paths: buildProductAvailabilityIndex() once, then O(1) lookup.
 * InstantCache is never consulted for qty.
 */

import { dedupeLotsList } from './lotSystem';
import { sameProductId } from './productIdUtils';
import { isLotIncludedInStockMetrics } from '../domain/inventory/lots/lotsIndex';
import { filterLotsForStore, DEFAULT_STORE_ID } from '../domain/inventory/stores/storeCatalog';

function normalizeLocation(location) {
  // Case-insensitive so 'Principal'/'principal' compare equal (mirrors lotLocationKey).
  return String(location || 'Principal').trim().toLowerCase() || 'principal';
}

export function normalizeLotsList(lots) {
  if (!lots) return [];
  return Array.isArray(lots) ? lots : Object.values(lots);
}

/** Sellable stock only — same rule as Stock/Inventaire (excludes date-expired). */
function isActiveLot(lot) {
  return isLotIncludedInStockMetrics(lot);
}

function readProductStockFallback(product) {
  if (!product) return 0;
  const candidates = [
    product.calculatedQuantity,
    product.stockNow,
    product.stock,
    product.quantity
  ];
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

function productKey(productId) {
  return String(productId ?? '');
}

/**
 * One-pass index for POS/catalog filters.
 * @returns {{
 *   getQuantity: (productId: string|number, product?: object|null) => number,
 *   hasLots: (productId: string|number) => boolean,
 *   qtyByProduct: Map<string, number>,
 *   hasLotsByProduct: Set<string>,
 *   dedupedLots: object[]
 * }}
 */
export function buildProductAvailabilityIndex(lots, stockLocation = 'Principal', options = {}) {
  const loc = normalizeLocation(stockLocation);
  const storeId = options.storeId || null;
  const scoped = storeId
    ? filterLotsForStore(normalizeLotsList(lots), storeId, DEFAULT_STORE_ID)
    : normalizeLotsList(lots);
  const dedupedLots = dedupeLotsList(scoped);
  const qtyByProduct = new Map();
  const hasLotsByProduct = new Set();

  for (const lot of dedupedLots) {
    if (!lot || lot.productId == null) continue;
    const pid = productKey(lot.productId);
    hasLotsByProduct.add(pid);
    if (normalizeLocation(lot.location) !== loc) continue;
    if (!isActiveLot(lot)) continue;
    qtyByProduct.set(pid, (qtyByProduct.get(pid) || 0) + (Number(lot.remainingQuantity) || 0));
  }

  return {
    dedupedLots,
    qtyByProduct,
    hasLotsByProduct,
    storeId: storeId || null,
    hasLots(productId) {
      return hasLotsByProduct.has(productKey(productId));
    },
    getQuantity(productId, product = null) {
      const pid = productKey(productId);
      if (hasLotsByProduct.has(pid)) {
        return qtyByProduct.get(pid) || 0;
      }
      // Lot universe present → product without lots is 0 (no catalog invention)
      if (dedupedLots.length > 0) {
        return 0;
      }
      return Math.max(0, readProductStockFallback(product));
    }
  };
}

/** True if this product has any lot rows (lot system owns stock). */
export function hasLotsForProduct(lots, productId) {
  return normalizeLotsList(lots).some((lot) => sameProductId(lot?.productId, productId));
}

export function hasLotsForProductAtLocation(lots, productId, stockLocation = 'Principal') {
  const loc = normalizeLocation(stockLocation);
  return normalizeLotsList(lots).some(
    (lot) =>
      sameProductId(lot?.productId, productId) &&
      normalizeLocation(lot.location) === loc
  );
}

/**
 * Einheitliche Verfügbarkeit — Lots sind die einzige Quelle sobald Lots existieren.
 * Optional: pass `availabilityIndex` from buildProductAvailabilityIndex for O(1).
 *
 * Public alias: getSellableQty (POS / Validation / Cart SSOT name).
 */
export function resolveProductAvailableQuantity(
  productId,
  stockLocation = 'Principal',
  { lots = [], product = null, availabilityIndex = null } = {}
) {
  if (availabilityIndex) {
    return availabilityIndex.getQuantity(productId, product);
  }

  const loc = normalizeLocation(stockLocation);
  const lotsList = dedupeLotsList(normalizeLotsList(lots));

  if (hasLotsForProduct(lotsList, productId)) {
    return sumAvailableQuantityFromLots(lotsList, productId, loc, { alreadyDeduped: true });
  }

  // Lot system active for this business — missing product lots means no sellable stock
  if (lotsList.length > 0) {
    return 0;
  }

  return Math.max(0, readProductStockFallback(product));
}

/** SSOT name for sellable qty at product+location (same as resolveProductAvailableQuantity). */
export function getSellableQty(productId, stockLocation = 'Principal', options = {}) {
  return resolveProductAvailableQuantity(productId, stockLocation, options);
}

/**
 * @param {object[]|object} lots
 * @param {string|number} productId
 * @param {string} [stockLocation]
 * @param {{ alreadyDeduped?: boolean }} [opts]
 */
export function sumAvailableQuantityFromLots(
  lots,
  productId,
  stockLocation = 'Principal',
  opts = {}
) {
  const loc = normalizeLocation(stockLocation);
  const list = opts.alreadyDeduped ? normalizeLotsList(lots) : dedupeLotsList(normalizeLotsList(lots));
  let sum = 0;
  for (const lot of list) {
    if (
      sameProductId(lot.productId, productId) &&
      normalizeLocation(lot.location) === loc &&
      isActiveLot(lot)
    ) {
      sum += Number(lot.remainingQuantity || 0);
    }
  }
  return sum;
}

/**
 * Verkaufswert am Standort — nur aus Lots (sellingUnitPrice × remaining).
 */
export function sumAvailableValueFromLots(lots, productId, stockLocation = 'Principal') {
  const loc = normalizeLocation(stockLocation);
  return dedupeLotsList(normalizeLotsList(lots))
    .filter(
      (lot) =>
        sameProductId(lot.productId, productId) &&
        normalizeLocation(lot.location) === loc &&
        isActiveLot(lot)
    )
    .reduce((sum, lot) => {
      const qty = Number(lot.remainingQuantity) || 0;
      const price = Number(lot.sellingUnitPrice || lot.purchaseUnitPrice) || 0;
      return sum + qty * price;
    }, 0);
}

/**
 * @param {object[]|object} lots
 * @param {string|number} productId
 */
export function filterLotsForProduct(lots, productId) {
  return dedupeLotsList(normalizeLotsList(lots)).filter((lot) =>
    sameProductId(lot.productId, productId)
  );
}

/**
 * @param {object[]|object} lots
 * @returns {(productId: string|number, stockLocation?: string) => number}
 */
export function createInventoryReaderFromLots(lots) {
  return (productId, stockLocation = 'Principal') =>
    sumAvailableQuantityFromLots(lots, productId, stockLocation);
}

/**
 * Leser mit Lots + Produkt-Fallback (nur ohne Lots-Universum).
 */
export function createInventoryReader({ lots = [], getProductById = null } = {}) {
  return (productId, stockLocation = 'Principal') =>
    resolveProductAvailableQuantity(productId, stockLocation, {
      lots,
      product: typeof getProductById === 'function' ? getProductById(productId) : null
    });
}
